/**
 * api/airportServices/getAirportList returns entries shaped like
 * `{ entityId, entityType, title, terminals, terminalSides, terminalInfo,
 *    availableCount, city, country, airportCode, pid, nearby, distanceKm }`.
 *
 * The airport name arrives as `title`, while most of the app reads
 * `airportName` (and a few screens read `title`). Normalizing once at the
 * service boundary keeps both spellings populated so existing callers — flight
 * / lounge / fasttrack / dutyfree airport pickers — keep rendering, and leaves
 * every other field on the entry untouched for callers that need the extras
 * (terminals, terminalSides, entityId, availableCount, ...).
 */
export const normalizeAirportListEntry = (entry: any): any => {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }
  const name = entry.title || entry.airportName || entry.name || '';
  return {
    ...entry,
    title: entry.title || name,
    airportName: entry.airportName || name || entry.city || '',
    airportCode: entry.airportCode || entry.airport_code || '',
    city: entry.city || '',
    country: entry.country || '',
    aiportCodeCity: entry.aiportCodeCity || entry.airportCode || '',
  };
};

/** Maps an airport list payload's `result`/`results` array through the normalizer. */
export const normalizeAirportList = (list: any): any[] =>
  Array.isArray(list) ? list.map(normalizeAirportListEntry) : [];

/**
 * `latlong` in localStorage is written by LocationService as
 * `{ latitude, longitude, timestamp }`. api/airportServices/getAirportList
 * accepts `latitude`/`longitude` to sort the list by proximity (it answers
 * `nearby` / `distanceKm` per entry), so the stored coordinates are read once
 * here and shared by every caller of that endpoint. Returns null when the
 * user has not granted location or the stored value is unusable, in which
 * case the params are simply left off the request.
 */
export const readStoredLatLong = (
  stored: string | null | undefined
): { latitude: number; longitude: number } | null => {
  if (!stored) {
    return null;
  }
  let parsed: any;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }
  const latitude = Number(parsed?.latitude);
  const longitude = Number(parsed?.longitude);
  if (!isFinite(latitude) || !isFinite(longitude)) {
    return null;
  }
  return { latitude, longitude };
};

/**
 * api/airportServices/getAirportList scopes the list with `type`. Callers that
 * come through AirportListService describe the scope as `servicetype`, so the
 * values the endpoint recognises as a `type` are mapped across here. Lounge and
 * fasttrack already reach the endpoint with `type` via
 * AirportServicesBookingService, so they are not listed.
 */
const SERVICE_TYPE_TO_TYPE: Record<string, string> = {
  dutyfree: 'dutyfree',
  meetngreet: 'meetngreet',
  porter: 'porter',
  airport_companion: 'airport_companion',
};

/**
 * Returns the `type` value for a `servicetype`, or null when the endpoint has
 * no `type` for that category and the param should be left off.
 */
export const serviceTypeToAirportListType = (
  serviceType: string | null | undefined
): string | null =>
  SERVICE_TYPE_TO_TYPE[(serviceType || '').trim().toLowerCase()] || null;
